import { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { formValueSelector } from 'redux-form'
import { useTranslation } from 'react-i18next'
import { Typography } from '@material-ui/core'
import ApartmentsService from '../../../services/apartments'
import { StateInterface } from '../../../types/states'

interface Estimate {
  estimatedEurM2: number
  estimatedRent?: number
  suggestedRent?: number
  low?: number
  high?: number
  confidence: 'high' | 'medium' | 'low'
  hasOwnData: boolean
  period: string
  band: string
}

interface Props {
  postalCode?: string
  squareMeters?: string
  rooms?: string
  buildYear?: string
  condition?: string
}

const RentRecommendation = ({
  postalCode,
  squareMeters,
  rooms,
  buildYear,
  condition
}: Props) => {
  const { t } = useTranslation()
  const [estimate, setEstimate] = useState<Estimate | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const mounted = useRef(true)
  // Debouncing cancels pending timers but not in-flight requests, so a slow earlier
  // response could still land after a newer one and show a rent for stale inputs.
  const latestRequest = useRef(0)
  useEffect(() => () => { mounted.current = false }, [])

  useEffect(() => {
    if (timer.current) {
      clearTimeout(timer.current)
    }
    const sqm = String(squareMeters || '').replace(',', '.') // tolerate Finnish comma decimals
    if (!postalCode || !(Number(sqm) > 0)) {
      latestRequest.current += 1 // discard anything already in flight
      setEstimate(null)
      return
    }
    timer.current = setTimeout(async () => {
      const requestId = ++latestRequest.current
      const isCurrent = () => mounted.current && requestId === latestRequest.current
      try {
        const result = await ApartmentsService.getRentEstimate(
          postalCode,
          sqm,
          rooms,
          buildYear,
          condition
        )
        if (isCurrent()) {
          setEstimate(result && result.suggestedRent ? result : null)
        }
      } catch {
        if (isCurrent()) {
          setEstimate(null) // endpoint down -> hide the banner, never block the flow
        }
      }
    }, 600)
    return () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [postalCode, squareMeters, rooms, buildYear, condition])

  if (!estimate || !estimate.suggestedRent) {
    return null
  }

  const source = t('apartment.rentWatch.basis')

  return (
    <div
      style={{
        background: '#FBF6EE',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 16
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span role='img' aria-label='idea' style={{ fontSize: 16 }}>
            💡
          </span>
          <Typography style={{ fontSize: 14, color: '#4b5563', fontWeight: 700 }}>
            {t('apartment.rentWatch.title')}
          </Typography>
        </div>
        <Typography
          component='span'
          style={{ fontSize: 28, fontWeight: 700, color: '#3d3d3d', lineHeight: 1 }}
        >
          {estimate.suggestedRent}
          <span style={{ fontSize: 17, fontWeight: 600, color: '#9c8f79', marginLeft: 5 }}>
            €/kk
          </span>
        </Typography>
      </div>
      <Typography
        style={{
          fontSize: 13.5,
          lineHeight: 1.5,
          color: '#8a919e',
          display: 'block',
          marginTop: 8
        }}
      >
        {source}
      </Typography>
    </div>
  )
}

const selector = formValueSelector('ApartmentInformationForm')

const mapStateToProps = (state: StateInterface) => ({
  postalCode: selector(state, 'postalCode'),
  squareMeters: selector(state, 'squareMeters'),
  rooms: selector(state, 'rooms'),
  buildYear: selector(state, 'buildYear'),
  condition: selector(state, 'condition')
})

export default connect(mapStateToProps)(RentRecommendation)
